import React, { useState, useRef } from 'react';

// Fallbacks only; both are served on every block. Every read goes through
// Number() because the runner derives its reaction-time ceiling as
// `timing().windowMs + 250`: a window arriving as a string would concatenate
// ("20000" + 250 -> "20000250") and every late first tap would be accepted as a
// real response. `||` rather than `??` so a served 0 falls back to the default
// instead of becoming an instant timeout that records the whole block as
// non-responses.
const DEFAULT_PRESENTATION_MS = 1000;
const DEFAULT_WINDOW_MS = 20000;
const BLOCKS = 9;
const VIEW = 100;

// The classical Corsi board is irregular, but an irregular layout that changed
// between trials would add a visual-search component that varies within the
// run. The arrangement is a fixed square grid, identical on every trial, so the
// only thing that varies is the sequence: index i sits at row floor(i / side),
// column i % side.
const sideOf = (gridSize) => Math.round(Math.sqrt(Number(gridSize) || BLOCKS));

function blockRect(index, gridSize) {
  const side = sideOf(gridSize);
  const cell = VIEW / side;
  const pad = cell * 0.14;
  return {
    x: (index % side) * cell + pad,
    y: Math.floor(index / side) * cell + pad,
    size: cell - pad * 2,
  };
}

// Drawn from gridSize alone. `litIndex` is supplied only by the presentation
// panel; the response panel never passes one, which is why the same board can
// serve both phases without the response phase being able to light anything.
// The accessible name of a block is its POSITION ("Block 5") — never its place
// in the sequence, which would put the answer in the DOM for a screen reader.
function Board({ gridSize, litIndex, onTap, disabled, tapped }) {
  const n = Number(gridSize) || BLOCKS;
  return (
    <svg
      width="min(72vw, 62vh)"
      height="min(72vw, 62vh)"
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      role="group"
      aria-label="Corsi board"
      style={{ touchAction: 'manipulation' }}
    >
      {Array.from({ length: n }, (_, i) => {
        const { x, y, size } = blockRect(i, n);
        const lit = i === litIndex;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={size}
            height={size}
            rx={size * 0.12}
            role={onTap ? 'button' : undefined}
            aria-label={onTap ? `Block ${i + 1}` : undefined}
            aria-disabled={onTap && disabled ? true : undefined}
            tabIndex={onTap && !disabled ? 0 : undefined}
            onClick={onTap && !disabled ? () => onTap(i) : undefined}
            onKeyDown={
              onTap && !disabled
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onTap(i);
                    }
                  }
                : undefined
            }
            fill={lit ? 'var(--accent-primary)' : 'var(--bg-elevated)'}
            stroke={tapped?.includes(i) ? 'var(--border-strong)' : 'var(--border-subtle)'}
            strokeWidth={tapped?.includes(i) ? 1.4 : 0.8}
            style={{ cursor: onTap && !disabled ? 'pointer' : 'default' }}
          />
        );
      })}
    </svg>
  );
}

// THE PRESENTATION PHASE. Lights one block at a time for
// presentation_interval_ms. It is a CueView with cuePersists: false, so
// RunnerShell UNMOUNTS it the instant the response phase begins — it is not
// merely hidden. A hidden-but-mounted panel is one CSS regression from being
// visible and is still readable in devtools, and leaving the sequence on screen
// turns a memory task into a copying test that the server cannot detect.
//
// The board it draws is not tappable: no block carries a role, a tabIndex or a
// handler, so no tap can be registered while the sequence is on screen.
//
// `params` is load-bearing and RunnerShell passes it. This panel lights one
// block per presentation_interval_ms while timing() sizes the whole stage as
// presentation_interval_ms x length, so the two MUST read the same number. When
// they did not — the shell passed only the stimulus and this view fell back to
// 1000ms — a served 600ms ended the stage after three of five blocks had been
// lit, and the response phase still required five taps. Those trials were
// unanswerable, the memory pillar floored, and the payload looked entirely
// normal. Never let this view's interval come from anywhere but the served
// params.
function CueView({ stimulus, params }) {
  const interval = Number(params?.presentation_interval_ms) || DEFAULT_PRESENTATION_MS;
  const sequence = stimulus?.sequence || [];
  const [step, setStep] = useState(0);

  React.useEffect(() => {
    if (step >= sequence.length) return undefined;
    const id = setTimeout(() => setStep((s) => s + 1), interval);
    return () => clearTimeout(id);
  }, [step, interval, sequence.length]);

  // Past the last block `step` is out of range and nothing is lit, so the board
  // is already dark when the stage ends at length x interval.
  return <Board gridSize={stimulus?.gridSize} litIndex={sequence[step] ?? null} />;
}

// THE RESPONSE PHASE. Reads gridSize and length and nothing else from the
// stimulus. It must never read stimulus.sequence — that is the answer, and it is
// served only because the participant has to be shown it.
function StimulusView({ stimulus, onFirstInteraction, onCommit, disabled }) {
  const [tapped, setTapped] = useState([]);
  // Guards against a tap landing after the answer has gone. The runner ignores a
  // second commit, but a late tap would still redraw the board as though it had
  // been counted.
  const committed = useRef(false);
  // How many taps to auto-commit at. Read off the served `length`, never off the
  // sequence. A stimulus with no usable length simply never auto-commits: a bare
  // `next.length >= target` with target 0 would fire on the very first tap and
  // submit a one-tap answer for every trial.
  const target = Number(stimulus?.length) || 0;

  const commit = (sequence) => {
    committed.current = true;
    onCommit({ sequence });
  };

  const tap = (index) => {
    if (disabled || committed.current) return;
    // Stamps the reaction time: end of the last presented block to the FIRST
    // tap. Idempotent in the runner, so later taps do not move it. It must
    // happen BEFORE the commit — commitComposite refuses an answer that has no
    // first interaction behind it, and the trial would silently submit as an
    // omission.
    onFirstInteraction();
    // Appended, never sorted, deduped or reordered: the order of the taps and
    // any repeated block ARE the measurement.
    const next = [...tapped, index];
    setTapped(next);
    // Auto-commit at the presented length. There is no partial credit — an exact
    // reproduction or nothing — so accepting more taps than were shown only
    // gives the participant a way to build a sequence they know is already wrong.
    if (target > 0 && next.length >= target) commit(next);
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', justifyItems: 'center' }}>
      <Board gridSize={stimulus?.gridSize} onTap={tap} disabled={disabled} tapped={tapped} />
      <button
        type="button"
        onClick={() => {
          // A short reproduction is a WRONG ANSWER, not missing data, and it is
          // sent. A trial with no taps at all is the one case that must NOT
          // commit: it submits as an all-null omission, which is what the runner
          // produces when onFirstInteraction is never called.
          if (disabled || committed.current || tapped.length === 0) return;
          commit(tapped);
        }}
        disabled={disabled || tapped.length === 0}
        style={{
          padding: 'var(--space-3) var(--space-5)', borderRadius: 'var(--radius-md)', fontWeight: 700, fontSize: 'var(--text-panel-size)',
          border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)',
          color: 'var(--text-primary)', fontFamily: 'inherit',
          cursor: tapped.length === 0 ? 'default' : 'pointer',
        }}
      >
        Done
      </button>
    </div>
  );
}

export const corsiSpanTask = {
  id: 'corsi-span',
  name: 'Block Order',
  pillar: 'memory',

  // Composite: the trial ends at commitComposite regardless, so the driver's
  // early-advance timer never applies here. Declared anyway — registry.test.js
  // requires an explicit pacing declaration from every module.
  holdsFullWindow: false,

  instructions: [
    'Some of the nine squares will light up, one after another.',
    'When they stop, tap them back in the same order.',
    'The squares go dark before you tap — that is the point.',
  ],

  // The board IS the response surface, so there are no button targets.
  responseMode: 'composite',
  targets: [],

  // THE SINGLE MOST IMPORTANT LINE IN THIS MODULE. RunnerShell unmounts CueView
  // at stimulus onset when this is false. The served sequence must not be
  // visible once taps are accepted, and nothing server-side can detect a
  // violation — a copying test produces a perfectly normal-looking score.
  cuePersists: false,

  // Empty deliberately. A worked example row would have to print a sequence next
  // to the answer that reproduces it, and TaskExamples renders it beside the
  // live board. TaskExamples returns null for an empty list.
  examples: [],

  // Never reached: a composite task builds its own answer object.
  toAnswer: () => ({}),

  // Presentation runs for length x presentation_interval_ms, so it depends on
  // THIS trial — the schedule runs 2, then 3, 4, 5, 6 across the run, and a
  // block-level duration would be wrong for every length but one. The response
  // window then starts at the end of the last presented block, which is exactly
  // where the runner starts the clock. windowMs is what the runner's
  // reaction-time ceiling (windowMs + 250) is derived from; a task that returned
  // no window would silently fall back to 30000ms.
  //
  // fixationMs and gapMs are fixed rather than subtracted from a served
  // interval: no isi_ms is served for this instrument. Do not harmonise with
  // flanker, stroop or pattern-comparison, whose conventions are mandated
  // per-instrument.
  timing: (params, trial) => {
    const interval = Number(params?.presentation_interval_ms) || DEFAULT_PRESENTATION_MS;
    const length = Number(trial?.stimulus?.length) || (trial?.stimulus?.sequence?.length ?? 0);
    return {
      fixationMs: 500,
      cueMs: interval * length,
      windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
      gapMs: 700,
    };
  },

  // The expected reproduction IS stimulus.sequence, and practice trials are the
  // only place an assessment may show feedback. The runner gates this on
  // trial.isPractice under an assessment driver (feedbackOnEveryTrial: false),
  // so it can never reach a scored trial. Compared against the answer object by
  // JSON string equality, so it returns one — which is also why order and
  // repeats decide it, and why no partial credit exists to display.
  practiceExpectation: (stimulus) => ({ sequence: stimulus?.sequence || [] }),

  CueView,
  StimulusView,
};
